/**
 * Date and time formatting utilities
 */

import { format, isValid, parseISO } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

/**
 * Formats a date to YYYY-MM-DD format in UTC
 * @param date - Date object or string to format
 * @returns Formatted date string in YYYY-MM-DD format
 * @example
 * formatDateYYYYMMDD(new Date("2024-01-17")) // "2024-01-17"
 */
export const formatDateYYYYMMDD = (date: Date | string): string => {
  const padStart = (value: number): string => value.toString().padStart(2, "0");
  const parsedDate = typeof date === "string" ? new Date(date) : date;

  return `${parsedDate.getUTCFullYear()}-${padStart(
    parsedDate.getUTCMonth() + 1
  )}-${padStart(parsedDate.getUTCDate())}`;
};

/**
 * Formats a date in human-readable format (e.g., "Jan 17, 2024")
 * @param date - Date object or string to format
 * @returns Human-readable date string, or empty string if invalid
 * @example
 * formatDateHumanReadable(new Date("2024-01-17")) // "Jan 17, 2024"
 */
export const formatDateHumanReadable = (date: Date | string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  };
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (parsedDate > new Date(0)) {
    return new Intl.DateTimeFormat("en-US", options).format(parsedDate);
  } else {
    return "";
  }
};

/**
 * Formats a date to display weekday and date in a user-friendly format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted string like "Wed, 17 Jan 2024" or empty string if invalid
 * @example
 * formatDateWithWeekday("2024-01-17") // "Wed, 17 Jan 2024"
 * formatDateWithWeekday(new Date("2024-01-17")) // "Wed, 17 Jan 2024"
 * formatDateWithWeekday(null) // ""
 */
export const formatDateWithWeekday = (
  date: string | Date | null | undefined
): string => {
  if (!date) return "";

  try {
    return formatInTimeZone(new Date(date), "UTC", "EEE, dd MMM yyyy");
  } catch (error) {
    console.error("Error formatting date with weekday:", error);
    return "";
  }
};

/**
 * Formats a timestamp with timezone information
 * @param value - String, Date, or null/undefined to format
 * @param tz - Timezone (default: "UTC")
 * @param formatString - Format string (default: "MM-dd-yyyy hh:mma z")
 * @returns Formatted timestamp string, or empty string if invalid
 * @example
 * formatTimestampHumanReadableTZ("2024-01-17T10:30:00Z") // "01-17-2024 10:30AM UTC"
 */
export function formatTimestampHumanReadableTZ(
  value: string | Date | null | undefined,
  tz: string = "UTC",
  formatString: string = "MM-dd-yyyy hh:mma z"
): string {
  if (!value) return "";
  let date: Date;
  if (typeof value === "string") {
    date = parseISO(value);
  } else if (value instanceof Date) {
    date = value;
  } else {
    return "";
  }
  if (!isValid(date)) return "";
  const zonedDate = toZonedTime(date, tz);
  return format(zonedDate, formatString);
}

/**
 * Formats a timestamp (number) to local time string
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted local time string
 * @example
 * formatTimestampToLocalTime(1705489800000) // "Jan 17, 2024, 10:30 AM"
 */
export const formatTimestampToLocalTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};